"use client";
import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function PharmacyPosSection({ section }) {
  const categoryTabs = [
    { name: "Analgesic", active: true, bg: "bg-blue-100 text-blue-900" },
    { name: "Dermatology", active: false, bg: "bg-purple-100 text-purple-900" },
    { name: "Otolaryngology", active: false, bg: "bg-blue-100 text-blue-900" },
    { name: "Blood Pressure", active: false, bg: "bg-purple-100 text-purple-900" },
    { name: "Pregnancy & Baby", active: false, bg: "bg-blue-100 text-blue-900" }
  ];

  const products = [
    { id: 1, name: "Acetratine 10 mg", image: "/odoo/unsplash/avatars/u1.jpg", qty: null },
    { id: 2, name: "Aspirin", image: "/odoo/unsplash/avatars/u2.jpg", qty: 2 },
    { id: 3, name: "CEFTAS 400 S TAB", image: "/odoo/unsplash/avatars/u3.jpg", qty: null },
    { id: 4, name: "Cetirizine 10 mg", image: "/odoo/unsplash/avatars/u4.jpg", qty: null },
    { id: 5, name: "Crocin 650", image: "/odoo/unsplash/avatars/u5.jpg", qty: null },
    { id: 6, name: "ENAPRIL 10MG TAB", image: "/odoo/unsplash/avatars/u6.jpg", qty: null },
    { id: 7, name: "Gift Card", image: "/odoo/unsplash/avatars/u7.jpg", qty: 1 },
    { id: 8, name: "Himalaya Baby Cream - Extra...", image: "/odoo/unsplash/avatars/u8.jpg", qty: 1 },
    { id: 9, name: "Himalaya Baby Powder 100 g", image: "/odoo/unsplash/avatars/u9.jpg", qty: null },
    { id: 10, name: "Ibuprofen 400 mg", image: "/odoo/unsplash/avatars/u10.jpg", qty: null },
    { id: 11, name: "Nasal Spray", image: "/odoo/unsplash/avatars/u11.jpg", qty: null },
    { id: 12, name: "Vitamin C Syrup", image: "/odoo/unsplash/avatars/u12.jpg", qty: null },
    { id: 13, name: "Antiseptic Solution", image: "/odoo/unsplash/avatars/u1.jpg", qty: null },
    { id: 14, name: "Skin Care Ointment", image: "/odoo/unsplash/avatars/u2.jpg", qty: null },
    { id: 15, name: "Pain Relief Gel", image: "/odoo/unsplash/avatars/u3.jpg", qty: null }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Heading & Feature Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8"
          >
            {/* Handwritten Heading with Sky Blue Marker Highlight */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.15]`}>
                <span>An </span>
                <span className="relative inline-block px-1">
                  efficient
                  <svg 
                    className="absolute -bottom-1 left-0 w-full h-4 text-sky-400 -z-10 overflow-visible" 
                    fill="none" 
                    viewBox="0 0 100 16"
                  >
                    <path 
                      d="M 0 8 L 100 8" 
                      stroke="#38BDF8" 
                      strokeWidth="14" 
                      strokeLinecap="round" 
                      opacity="0.85" 
                    />
                  </svg>
                </span>
                <span> Point of</span>
                <br />
                <span>Sale, simplified</span>
              </h2>
            </div>

            {/* Subtext Paragraphs */}
            <div className="space-y-5 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Intuitive POS system.</strong> Process transactions quickly and accurately, and use a barcode scanner for fast, error-free checkouts.
              </p>
              <p>
                <strong className="text-white font-bold">Promotions, combos, and campaigns</strong> such as “Buy 2 get 1 free” allow you to maximize customer loyalty.
              </p>
              <p>
                <strong className="text-white font-bold">Payment methods.</strong> Accept payments using different methods, such as cash, bank, or customer accounts.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Pharmacy POS Dashboard Window & Top-Right Sparkle Doodle */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative"
          >
            {/* Top-Right Sky Blue Sparkle Doodle */}
            <div className="absolute -top-6 -right-2 sm:-right-6 z-30 select-none pointer-events-none text-sky-400">
              <svg className="w-10 h-10 sm:w-12 sm:h-12" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="20" y1="4" x2="20" y2="12" />
                <line x1="34" y1="10" x2="26" y2="16" />
                <line x1="38" y1="24" x2="28" y2="24" />
                <line x1="32" y1="34" x2="24" y2="28" />
              </svg>
            </div>

            {/* Background Soft Shadow Arc behind top-left */}
            <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-slate-100/10 blur-2xl -z-10 pointer-events-none" />

            {/* Main Pharmacy POS Window */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 overflow-hidden text-slate-800 font-sans text-xs">
              
              {/* Header Bar */}
              <div className="bg-white border-b border-slate-200 px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
                
                {/* Left quick actions */}
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded border border-slate-300 flex items-center justify-center font-bold text-slate-600 hover:bg-slate-50 cursor-pointer">
                    +
                  </div>
                  <div className="px-2 py-0.5 bg-slate-100 rounded border border-slate-200 text-[11px] font-bold text-slate-700">
                    301
                  </div>
                </div>

                {/* Center Brand */}
                <div className="font-bold text-slate-900 text-sm sm:text-base tracking-tight">
                  odoo
                </div>

                {/* Right Search & Controls */}
                <div className="flex items-center gap-2">
                  <div className="relative w-32 sm:w-44">
                    <input 
                      type="text" 
                      placeholder="Search products..." 
                      readOnly 
                      className="w-full text-[11px] px-2.5 py-1 pr-6 border border-slate-200 rounded bg-slate-50 text-slate-400"
                    />
                    <svg className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-[10px]">
                    A
                  </div>
                  <div className="text-slate-600 text-sm cursor-pointer font-bold">
                    ≡
                  </div>
                </div>

              </div>

              {/* Main Content Grid: Cart (Left 4.5 cols) & Product Catalog (Right 7.5 cols) */}
              <div className="grid grid-cols-1 md:grid-cols-12 bg-slate-100/60 min-h-[380px]">
                
                {/* Left Panel: Cart & Keypad */}
                <div className="col-span-1 md:col-span-5 border-b md:border-b-0 md:border-r border-slate-200 p-2 sm:p-3 bg-white flex flex-col justify-between space-y-2">

                  
                  {/* Cart Items List */}
                  <div className="space-y-2 overflow-y-auto max-h-[160px] text-[10px]">
                    <div className="p-1.5 rounded bg-slate-50 border border-slate-100 flex justify-between items-start">
                      <div>
                        <div className="font-bold text-slate-800">Aspirin</div>
                        <div className="text-[9px] text-slate-400 font-medium">
                          <strong className="text-slate-700">2.00</strong> x 3.62 € / Units
                        </div>
                      </div>
                      <div className="font-bold text-slate-800">7.24 €</div>
                    </div>

                    <div className="p-1.5 rounded bg-emerald-50/80 border border-emerald-100 flex justify-between items-start">
                      <div>
                        <div className="font-bold text-slate-900">
                          Himalaya Baby Cream - Extra Soft & Gentle, Paraben Free, 100 ml Tube
                        </div>
                        <div className="text-[9px] text-slate-600 mt-0.5">
                          <strong className="text-slate-800">1.00</strong> x 19.36 € / Units
                        </div>
                        <div className="text-[8px] text-slate-500 font-mono">Lot Number 123</div>
                      </div>
                      <div className="font-bold text-slate-900 shrink-0 ml-1">19.36 €</div>
                    </div>

                    <div className="p-1.5 rounded bg-slate-50 border border-slate-100 flex justify-between items-start">
                      <div>
                        <div className="font-bold text-slate-800">Gift Card</div>
                      </div>
                      <div className="font-bold text-slate-800">50.00 €</div>
                    </div>
                  </div>

                  {/* Cart Totals Summary */}
                  <div className="pt-2 border-t border-slate-200 text-[10px] space-y-0.5">
                    <div className="flex justify-between text-slate-500">
                      <span>Taxes</span>
                      <span>4.62 €</span>
                    </div>
                    <div className="flex justify-between font-bold text-slate-900 text-xs sm:text-sm pt-0.5">
                      <span>Total</span>
                      <span>76.60 €</span>
                    </div>
                  </div>

                  {/* Customer & Actions Bar */}
                  <div className="flex items-center gap-1 text-[9px] border-t border-b border-slate-100 py-1.5">
                    <button className="flex-1 py-1 px-1.5 bg-slate-50 border border-slate-200 rounded text-slate-700 font-semibold truncate text-center">
                      Natalie Jones
                    </button>
                    <button className="py-1 px-1.5 bg-slate-50 border border-slate-200 rounded text-slate-600 font-medium truncate">
                      Internal Note
                    </button>
                    <button className="py-1 px-1.5 bg-slate-50 border border-slate-200 rounded text-slate-600 font-medium truncate">
                      Actions
                    </button>
                  </div>

                  {/* Numpad Keypad */}
                  <div className="grid grid-cols-4 gap-1 text-[10px]">
                    {["1", "2", "3", "Qty", "4", "5", "6", "%", "7", "8", "9", "Price", "+/-", "0", ".", "⌫"].map((key, kIdx) => (
                      <button 
                        key={kIdx} 
                        className={`py-1.5 rounded border text-center font-semibold transition-colors ${
                          key === "Qty" || key === "%" || key === "Price" 
                            ? "bg-sky-50 border-sky-200 text-sky-800" 
                            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>

                  {/* Purple Checkout Payment Button */}
                  <button className="w-full py-2.5 bg-[#875A7B] text-white font-bold rounded-xl text-xs shadow-xs hover:bg-[#734b68] transition-colors">
                    Payment
                  </button>

                </div>

                {/* Right Panel: Category Pills & Product Grid */}
                <div className="col-span-1 md:col-span-7 p-2 sm:p-3 space-y-2 flex flex-col justify-between">

                  
                  {/* Category Pills Header */}
                  <div className="flex items-center gap-1 overflow-x-auto pb-1 text-[9px] font-semibold scrollbar-none">
                    {categoryTabs.map((tab, tIdx) => (
                      <span 
                        key={tIdx} 
                        className={`px-2 py-1 rounded-md shrink-0 cursor-pointer ${tab.bg}`}
                      >
                        {tab.name}
                      </span>
                    ))}
                  </div>

                  {/* Product Cards 5x3 Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 overflow-y-auto max-h-[300px]">
                    {products.map((p) => (
                      <div 
                        key={p.id} 
                        className="bg-white rounded-lg border border-slate-200/90 p-1.5 relative flex flex-col justify-between hover:shadow-sm transition-shadow cursor-pointer group"
                      >
                        {/* Qty Badge */}
                        {p.qty && (
                          <span className="absolute top-1 right-1 bg-slate-800 text-white font-bold text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center z-10">
                            {p.qty}
                          </span>
                        )}

                        {/* Product Image */}
                        <div className="w-full h-12 rounded bg-slate-50 overflow-hidden mb-1 flex items-center justify-center border border-slate-100">
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>

                        {/* Product Name */}
                        <div className="text-[9px] font-bold text-slate-800 leading-tight line-clamp-2">
                          {p.name}
                        </div>
                      </div>
                    ))}
                  </div>

                </div>

              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Inventory management
   ───────────────────────────────────────────── */
export function PharmacyInventorySection({ section }) {
  const stockItems = [
    {
      name: "Acetratine 10 mg",
      price: "Price: $ 60.00",
      onHand: "On hand: 87.00 Units",
      image: "/odoo/unsplash/avatars/u1.jpg"
    },
    {
      name: "CEFTAS 400 5 TAB",
      price: "Price: $ 100.00",
      onHand: "On hand: 95.00 Units",
      image: "/odoo/unsplash/avatars/u3.jpg"
    },
    {
      name: "ENAPRIL 10MG TAB",
      price: "Price: $ 115.00",
      onHand: "On hand: 36.00 Units",
      image: "/odoo/unsplash/avatars/u6.jpg"
    },
    {
      name: "Himalaya Baby Cream - Extra Soft & Gentle, Paraben Free, 100 ml Tube",
      price: "Price: $ 160.00",
      onHand: "On hand: 47.00 Units",
      image: "/odoo/unsplash/avatars/u8.jpg"
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        
        {/* Centered Section Header with Pointing Hand / Arrow Doodle */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14 max-w-4xl mx-auto space-y-4 relative"
        >
          {/* Top Cursor Pointer Arrow Pointing Downward Doodle */}
          <div className="flex justify-center select-none mb-3">
            <div className="w-16 h-16 sm:w-20 sm:h-20 relative">
              <svg viewBox="0 0 100 100" fill="none" className="w-full h-full drop-shadow-md">
                {/* Yellow/Amber Offset Fill Layer */}
                <g transform="translate(3, 3)" fill="#FFB703">
                  <path d="M 50 76 L 72 34 L 56 44 L 64 18 L 54 14 L 46 40 L 28 34 Z" />
                  {/* Yellow Sparks Underlay */}
                  <circle cx="50" cy="86" r="3" />
                  <circle cx="64" cy="83" r="3" />
                  <circle cx="36" cy="83" r="3" />
                </g>

                {/* Dark Navy Cursor Arrow Outline Layer */}
                <g stroke="#0F172A" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
                  {/* Mouse Pointer Arrow (Pointing Straight Downward) */}
                  <path d="M 50 76 L 72 34 L 56 44 L 64 18 L 54 14 L 46 40 L 28 34 Z" />

                  {/* Click Burst Lines at the Bottom Tip of Arrow */}
                  <path d="M 50 82 L 50 90" strokeWidth="4" />
                  <path d="M 60 80 L 68 86" strokeWidth="4" />
                  <path d="M 40 80 L 32 86" strokeWidth="4" />
                  <path d="M 64 74 L 74 76" strokeWidth="4" />
                  <path d="M 36 74 L 26 76" strokeWidth="4" />
                </g>
              </svg>
            </div>
          </div>

          {/* Cursive Title */}
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span className="relative inline-block px-1">
              Inventory
              <svg 
                className="absolute -bottom-1 left-0 w-full h-3.5 text-amber-400 overflow-visible" 
                viewBox="0 0 100 14" 
                fill="none"
              >
                <path 
                  d="M 0 6 L 100 6 M 5 11 L 95 11" 
                  stroke="#FBBF24" 
                  strokeWidth="4" 
                  strokeLinecap="round" 
                />
              </svg>
            </span>
            <span> management</span>
          </h2>

          {/* Subtext Paragraph */}
          <p className="text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed max-w-4xl mx-auto font-normal">
            <strong className="text-white font-bold">Use real-time stock tracking</strong> to avoid stockouts or overstocking. Maintain a clear record of product origin, expiration dates, and lot numbers to comply with regulations.
          </p>
        </motion.div>

        {/* Double Overlapping Mockup Area (Inventory Dashboard + On-Hand Stock Grid) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative max-w-[1400px] mx-auto pt-6"
        >
          {/* Left Floating Pill/Medicine Bottle Doodle Icon */}
          <div className="absolute -top-4 left-4 sm:left-12 z-30 select-none pointer-events-none transform -rotate-12">
            <div className="w-14 h-16 sm:w-18 sm:h-20 bg-amber-400 border-3 border-slate-900 rounded-2xl flex flex-col items-center justify-center p-2 shadow-lg">
              <div className="w-8 h-2 bg-slate-900 rounded-full mb-1"></div>
              <div className="w-full h-full border-2 border-slate-900 rounded-lg bg-amber-300 flex flex-col items-center justify-center gap-1 p-1">
                <div className="w-4 h-1 bg-slate-900 rounded-full"></div>
                <div className="w-5 h-1 bg-slate-900 rounded-full"></div>
                <div className="w-3 h-1 bg-slate-900 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Back Window: Odoo Inventory Overview Dashboard */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-slate-200 overflow-hidden text-slate-800 font-sans text-xs w-[94%] mx-auto">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 font-bold text-amber-600">
                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                    <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-900 text-sm">Inventory</span>
                </div>
                <nav className="hidden sm:flex items-center gap-3 text-slate-600 text-[11px] font-medium">
                  <span className="font-bold text-slate-900">Overview</span>
                  <span>Operations</span>
                  <span>Products</span>
                  <span>Reporting</span>
                  <span>Configuration</span>
                </nav>
              </div>

              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-slate-600 hidden sm:inline">My Company</span>
                <div className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-[10px] flex items-center justify-center">
                  A
                </div>
              </div>
            </div>

            {/* Dashboard Cards Grid */}
            <div className="p-4 sm:p-6 bg-slate-50 grid grid-cols-2 gap-4 pb-20">
              
              {/* POS Orders Card */}
              <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center justify-between font-bold text-slate-800 text-xs">
                  <span>PoS Orders</span>
                  <span className="px-2 py-0.5 bg-[#875A7B] text-white rounded text-[10px]">8 To Deliver</span>
                </div>
                <div className="flex items-end gap-1.5 h-16 pt-2">
                  <div className="w-1/4 bg-rose-400 h-[65%] rounded-t"></div>
                  <div className="w-1/4 bg-amber-400 h-[35%] rounded-t"></div>
                  <div className="w-1/4 bg-emerald-400 h-[90%] rounded-t"></div>
                  <div className="w-1/4 bg-emerald-500 h-[45%] rounded-t"></div>
                </div>
                <div className="flex justify-between text-[10px] text-slate-500 border-t border-slate-100 pt-1.5 font-medium">
                  <span>Late: 2</span>
                  <span>Operations: 8</span>
                </div>
              </div>

              {/* Delivery Orders Card */}
              <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center justify-between font-bold text-slate-800 text-xs">
                  <span>Delivery Orders</span>
                  <span className="px-2 py-0.5 bg-[#875A7B] text-white rounded text-[10px]">3 To Deliver</span>
                </div>
                <div className="flex items-end gap-1.5 h-16 pt-2">
                  <div className="w-1/4 bg-rose-400 h-[30%] rounded-t"></div>
                  <div className="w-1/4 bg-amber-500 h-[85%] rounded-t"></div>
                  <div className="w-1/4 bg-emerald-400 h-[40%] rounded-t"></div>
                  <div className="w-1/4 bg-emerald-500 h-[40%] rounded-t"></div>
                </div>
                <div className="flex justify-between text-[10px] text-slate-500 border-t border-slate-100 pt-1.5 font-medium">
                  <span>Waiting: 3</span>
                  <span>Late: 2</span>
                </div>
              </div>

            </div>
          </div>

          {/* Front Overlapping Window: 2x2 On-Hand Stock Grid */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 p-4 sm:p-5 text-slate-800 font-sans text-xs -mt-16 relative z-20 max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {stockItems.map((item, sIdx) => (
                <div 
                  key={sIdx}
                  className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-between gap-3 shadow-2xs hover:shadow-xs transition-shadow"
                >
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-start gap-1 font-bold text-slate-800 text-xs leading-snug truncate">
                      <span className="text-slate-400 text-xs shrink-0">☆</span>
                      <span className="truncate">{item.name}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-medium pl-3">
                      {item.price}
                    </div>
                    <div className="text-[10px] text-emerald-700 font-semibold pl-3">
                      {item.onHand}
                    </div>
                  </div>

                  {/* Product Image Thumbnail */}
                  <div className="w-12 h-12 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                </div>
              ))}
            </div>
          </div>

        </motion.div>

        {/* 2-Column Subtext Below Mockup */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 text-slate-300 text-base sm:text-lg leading-relaxed mt-12 sm:mt-16 max-w-[1400px] w-full mx-auto font-normal"
        >
          <div>
            <p>
              <strong className="text-white font-bold">Efficient product search</strong> allows you to locate items by name, code, or category to optimize order fulfillment.
            </p>
          </div>

          <div>
            <p>
              <strong className="text-white font-bold">Automated purchase orders</strong> ensure timely replenishment and keep your stock on optimal levels at all times.
            </p>
          </div>
        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Testimonial Quote Callout
   ───────────────────────────────────────────── */
export function PharmacyTestimonialSection({ section }) {
  return (
    <section className="py-12 sm:py-16 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        
        {/* Top Curved Dashed Arrow Doodle */}
        <div className="flex justify-center select-none mb-6">
          <svg className="w-12 h-14 text-slate-400 opacity-80" viewBox="0 0 40 60" fill="none">
            <path 
              d="M 24 4 C 32 20, 28 36, 16 48 M 10 40 L 16 48 L 24 44" 
              stroke="currentColor" 
              strokeWidth="3" 
              strokeDasharray="4 4" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
          </svg>
        </div>

        {/* Testimonial Light Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[radial-gradient(circle_at_20%_0%,rgba(255,255,255,0.30),transparent_40%),radial-gradient(circle_at_100%_0%,rgba(255,255,255,0.05),transparent_30%)] rounded-3xl p-6 sm:p-10 border border-slate-200/80 shadow-md text-slate-800 relative max-w-[1400px] mx-auto"
        >
          {/* Top Left Yellow Quote Badge */}
          <div className="absolute -top-5 left-8 sm:left-12 select-none">
            <div className="w-12 h-12 rounded-2xl bg-amber-400 border-2 border-slate-900 flex items-center justify-center shadow-md transform -rotate-6">
              <svg className="w-7 h-7 text-slate-900 fill-current" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center pt-3">
            
            {/* Quote Text (Left 9 cols) */}
            <div className="md:col-span-9">
              <p className="font-semibold text-white text-lg sm:text-xl lg:text-2xl leading-relaxed tracking-tight">
                “Our inventory losses have decreased significantly. In fact, we can confidently now say that there are almost no losses occurring anymore.”
              </p>
            </div>

            {/* Author Profile (Right 3 cols) */}
            <div className="md:col-span-3 flex md:flex-col items-center md:items-end text-left md:text-right gap-3 md:gap-2 border-t md:border-t-0 border-slate-200/80 pt-4 md:pt-0">
              <img 
                src="/odoo/unsplash/avatars/u8.jpg" 
                alt="Narmandakh Buyan" 
                className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm shrink-0" 
              />
              <div>
                <div className="font-bold text-[#fafafa] text-sm sm:text-base leading-tight">
                  Narmandakh Buyan
                </div>
                <div className="text-xs text-[#fafafa]/40 font-medium mt-0.5 space-y-0.5">
                  <div>Head of MUB channel,</div>
                  <div className="font-semibold text-[#fafafa]/60">Monos Pharma Trade</div>
                </div>
              </div>
            </div>

          </div>

        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   4. Streamlined sales
   ───────────────────────────────────────────── */
export function PharmacySalesSection({ section }) {
  const catalogProducts = [
    {
      name: "Acetratine 10 mg",
      price: "Price: 60.00 €",
      onHand: "On hand: 12.00 Units",
      image: "/odoo/unsplash/avatars/u1.jpg"
    },
    {
      name: "Antiseptic Cream 200 g",
      price: "Price: 12.00 €",
      onHand: "On hand: 150.00 Units",
      image: "/odoo/unsplash/avatars/u2.jpg"
    },
    {
      name: "Baby Cream - Extra Soft, Paraben Free, 100 ml",
      price: "Price: 16.00 €",
      onHand: "On hand: 30.00 Units",
      image: "/odoo/unsplash/avatars/u8.jpg"
    },
    {
      name: "Baby Powder 100 g",
      price: "Price: 85.00 €",
      onHand: "On hand: 70.00 Units",
      image: "/odoo/unsplash/avatars/u9.jpg"
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Cursive Title & Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8"
          >
            {/* Title with Salmon/Pink Underline */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.15]`}>
                <span className="relative inline-block px-1">
                  Streamlined
                  <svg 
                    className="absolute -bottom-1 left-0 w-full h-3.5 text-rose-400 overflow-visible" 
                    viewBox="0 0 100 14" 
                    fill="none"
                  >
                    <path 
                      d="M 0 6 L 100 6" 
                      stroke="#FB7185" 
                      strokeWidth="5" 
                      strokeLinecap="round" 
                    />
                  </svg>
                </span>
                <span> sales</span>
              </h2>
            </div>

            {/* Copy Paragraphs */}
            <div className="space-y-6 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Set up multiple pricelists for customers,</strong> regular customers, and B2B clients such as nursing homes or hospitals.
              </p>
              <p>
                <strong className="text-white font-bold">Create sales orders in a flash.</strong> Right pricelists and discounts are automatically applied.
              </p>
              <p>
                <strong className="text-white font-bold">Assign lot numbers to sales orders</strong> to reserve products and mark them ready for delivery.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Overlapping Sales Order & Product Selector Catalog Windows */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative"
          >
            {/* Back Window: Odoo Sales Order Form */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-slate-200 overflow-hidden text-slate-800 font-sans text-xs w-[94%]">
              
              {/* Navbar */}
              <div className="bg-white border-b border-slate-200 px-3 sm:px-4 py-2 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-3 sm:gap-4 overflow-x-auto">
                  <div className="flex items-center gap-1 font-bold text-amber-700">
                    <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7 1a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <span className="text-slate-900 text-xs sm:text-sm">Sales</span>
                  </div>

                  <nav className="hidden sm:flex items-center gap-3 text-slate-600 text-[11px] font-medium">
                    <span>Orders</span>
                    <span>To Invoice</span>
                    <span>Products</span>
                    <span>Reporting</span>
                    <span>Configuration</span>
                  </nav>
                </div>

                <div className="flex items-center gap-2 shrink-0 text-[11px]">
                  <span className="text-slate-600 hidden sm:inline">My Company</span>
                  <div className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-[10px] flex items-center justify-center">
                    A
                  </div>
                </div>
              </div>

              {/* Action Bar & Stepper */}
              <div className="px-3 sm:px-4 py-2 bg-slate-50 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <button className="px-2.5 py-1 bg-[#875A7B] text-white font-semibold rounded text-[11px]">
                    Create Invoice
                  </button>
                  <button className="px-2 py-1 bg-white border border-slate-200 text-slate-700 font-medium rounded text-[11px]">
                    Send by Email
                  </button>
                  <button className="px-2 py-1 bg-white border border-slate-200 text-slate-700 font-medium rounded text-[11px]">
                    Preview
                  </button>
                </div>

                {/* Status Stepper */}
                <div className="flex items-center text-[10px] font-medium bg-slate-200/60 rounded px-2 py-0.5 text-slate-500 gap-1">
                  <span>Quotation</span>
                  <span>›</span>
                  <span>Quotation Sent</span>
                  <span>›</span>
                  <span className="font-bold text-teal-800 bg-white px-1.5 py-0.5 rounded shadow-2xs">Sales Order</span>
                </div>
              </div>

              {/* Order Form Body */}
              <div className="p-3 sm:p-4 bg-white space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">S00003</h3>
                    <div className="text-[11px] text-slate-500 mt-1">
                      <span className="font-semibold text-slate-700">Patient Name: </span>
                      <span className="text-sky-700 font-medium">Glaxo Smith Kline</span>
                      <div className="text-[10px] text-slate-400">Moore Drive 5, Durham NC 27703, United States</div>
                    </div>
                  </div>

                  <div className="text-right text-[10px] text-slate-500 space-y-0.5">
                    <div><span className="font-medium text-slate-600">Doctor:</span> --</div>
                    <div><span className="font-medium text-slate-600">Order Date:</span> 02/05/2025 16:21:03</div>
                    <div><span className="font-medium text-slate-600">Pricelist:</span> Wholesale (B2B) (EUR)</div>
                    <div><span className="font-medium text-slate-600">Payment Terms:</span> 21 Days</div>
                  </div>
                </div>

                {/* Lines Table */}
                <div className="border-t border-slate-100 pt-2 text-[10px]">
                  <div className="flex gap-2 font-bold text-slate-700 border-b border-slate-200 pb-1">
                    <span>Order Lines</span>
                    <span className="text-slate-400 font-normal">Other Info</span>
                  </div>
                  <div className="py-1 text-slate-600 space-y-1 font-mono text-[9px] pb-12">
                    <div>Sudocrem Antiseptic Cream 250 g</div>
                    <div>Himalaya Baby Powder 100 g</div>
                    <div>Himalaya Baby Cream - Extra Soft & Gentle, Paraben Free...</div>
                    <div>Crocin 650</div>
                  </div>
                </div>

              </div>

            </div>

            {/* Front Overlapping Window: Product Selector Cards */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 p-3 sm:p-4 text-slate-800 font-sans text-xs -mt-20 ml-auto w-[90%] relative z-20">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {catalogProducts.map((p, idx) => (
                  <div 
                    key={idx}
                    className="bg-white rounded-xl border border-slate-200/90 p-2.5 flex items-center justify-between gap-2 shadow-2xs hover:shadow-xs transition-shadow"
                  >
                    <div className="space-y-0.5 flex-1 min-w-0">
                      <div className="flex items-start gap-1 font-bold text-slate-800 text-[11px] leading-tight truncate">
                        <span className="text-slate-400 shrink-0">☆</span>
                        <span className="truncate">{p.name}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 pl-3">
                        {p.price}
                      </div>
                      <div className="text-[9px] text-emerald-700 font-semibold pl-3">
                        {p.onHand}
                      </div>
                    </div>

                    <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   5. Secure and efficient multi-pharmacy management
   ───────────────────────────────────────────── */
export function PharmacyMultiManagementSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-14"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span className="relative inline-block px-1">
              Secure
              <svg 
                className="absolute -bottom-1 left-0 w-full h-3.5 text-sky-400 overflow-visible" 
                viewBox="0 0 100 14" 
                fill="none"
              >
                <path 
                  d="M 0 8 C 20 2, 40 14, 60 4 C 80 14, 100 6, 100 6" 
                  stroke="#38BDF8" 
                  strokeWidth="4.5" 
                  strokeLinecap="round" 
                />
              </svg>
            </span>
            <span> and </span>
            <span className="relative inline-block px-1">
              efficient
              <svg 
                className="absolute -bottom-1 left-0 w-full h-3.5 text-sky-400 overflow-visible" 
                viewBox="0 0 100 14" 
                fill="none"
              >
                <path 
                  d="M 0 8 C 20 2, 40 14, 60 4 C 80 14, 100 6, 100 6" 
                  stroke="#38BDF8" 
                  strokeWidth="4.5" 
                  strokeLinecap="round" 
                />
              </svg>
            </span>
            <span> multi-pharmacy management</span>
          </h2>
        </motion.div>

        {/* 3-Column Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 text-slate-300 text-base sm:text-lg leading-relaxed mb-12 sm:mb-16 font-normal"
        >
          <div>
            <p>
              <strong className="text-white font-bold">Simplify administrative tasks</strong> by managing inventory, cash registers, invoicing, and more for multiple pharmacies.
            </p>
          </div>

          <div>
            <p>
              <strong className="text-white font-bold">Streamline procurement processes</strong> with efficient bulk ordering capabilities.
            </p>
          </div>

          <div>
            <p>
              <strong className="text-white font-bold">Protect sensitive patient and business data</strong> with Odoo's robust security measures.
            </p>
          </div>
        </motion.div>

        {/* Full Combined Graphic Display */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative max-w-[1200px] mx-auto pt-4 flex justify-center"
        >
          <div className="w-full relative overflow-hidden rounded-2xl sm:rounded-3xl">
            <img 
              src="/odoo/industries/pharmacy/multi-pharmacy-full.png" 
              alt="Multi-Pharmacy Management Overview" 
              className="w-full h-auto object-contain rounded-2xl sm:rounded-3xl" 
            />
          </div>
        </motion.div>

      </div>
    </section>
  );
}

export default PharmacyPosSection;
